import useSWR from "swr";
import { fetcher } from "./fetcher";
import { useRecoilState } from "recoil";
import { detailLoadingState } from "states/detailLoading";
import { KOREA, US } from "utils/constant/ITME_INFO";
import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import usePriority from "./priority";
import { useDetailAI } from "./detailAI";
import { useQuery } from "@tanstack/react-query";

export type DetailDataFallback = {
    fallbackDetail?: any[] | null;
    fallbackScore?: any | null;
    fallbackHalt?: { isHalt?: boolean } | null;
};

export const useDetailData = (
    ticker: string | undefined,
    fallback?: DetailDataFallback
) => {
    // 주요 데이터 요청 (GSSP initialDetailData 가 있으면 첫 페인트부터 사용)
    const { data: detailData, isValidating: isDetailValidating } = useSWR(
        ticker ? `/api/detail/${ticker}` : null,
        fetcher,
        fallback?.fallbackDetail
            ? { fallbackData: fallback.fallbackDetail }
            : undefined
    );

    // 정지 상태 여부 데이터 요청
    const { data: haltData, isValidating: isHaltValidating } = useSWR(
        detailData ? `/api/haltyn?itemCode=${detailData[0]?.ITEM_CD}` : null,
        fetcher,
        fallback?.fallbackHalt
            ? { fallbackData: fallback.fallbackHalt }
            : undefined
    );

    // 총점 데이터 score 추가
    const { data: totalData, isValidating: isTotalValidating } = useSWR(
        detailData
            ? `/api/detail/score?ticker=${ticker}&itemCode=${detailData[0]?.ITEM_CD}`
            : null,
        fetcher,
        fallback?.fallbackScore
            ? { fallbackData: fallback.fallbackScore }
            : undefined
    );

    // isValidating 단독이면 fallback 이 있어도 로딩으로 잡혀 SSR/첫 HTML 이 막힌다.
    // 데이터가 없을 때만 로딩으로 본다.
    const isLoading =
        (!detailData && isDetailValidating) ||
        (!!detailData && !haltData && isHaltValidating) ||
        (!!detailData && !totalData && isTotalValidating);

    const isHalt = haltData?.isHalt;
    const Cat = detailData?.[0]?.CAT;
    const Loc = detailData?.[0]?.LOC;

    // 유효한 Score 여부 체크
    // detailData(=CAT/LOC)가 도착하면 그 값으로 확정한다.
    // isValidating(백그라운드 재검증)으로 false 강제 시, 캐시가 있어도 진입 순간
    // 탭 경로로 렌더됐다가 재검증 완료 후 차트로 바뀌는 깜빡임(FOUC)이 발생한다.
    const isValidScoreStock = !detailData
        ? false
        : Cat === "Index" ||
          isHalt ||
          !(Cat === "Stock" && (Loc === KOREA || Loc === US));

    return {
        detailData: detailData?.[0],
        scoreData: totalData,
        prevDayData: detailData?.[1],
        isLoading,
        isHalt,
        isValidScoreStock,
    };
};


export const useFinanceData = (ticker: string | undefined, ITEM_CD: number, enable = true,): [any, boolean] => {
    const { data, isLoading } = useQuery({
        queryKey: ["detail", "fin", ticker, ITEM_CD],
        queryFn: () => fetcher(`/api/detail/fin?ticker=${ticker}&score=true&itemCode=${ITEM_CD}`),
        enabled: enable,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}
export const useIndustryMMTData = (ticker: string | undefined, ITEM_CD: number, enable = true, isSummary = false): [any, boolean] => {
    const router = useRouter();
    const { data, isLoading } = useQuery({
        queryKey: ["detail", "momentum", ticker, ITEM_CD, isSummary],
        queryFn: () => fetcher(`/api/detail/momentum?ticker=${ticker}&score=true&itemCode=${ITEM_CD}${isSummary ? "&isSummary=true" : ""}`),
        enabled: enable,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}
export const useSharpeData = (ticker: string | undefined, ITEM_CD: number, enable = true, isSummary = false): [any, boolean] => {
    const router = useRouter();
    const { data, isLoading } = useQuery({
        queryKey: ["detail", "sharpe", ticker, ITEM_CD, isSummary],
        queryFn: () => fetcher(`/api/detail/sharpe?ticker=${ticker}&score=true&itemCode=${ITEM_CD}${isSummary ? "&isSummary=true" : ""}`),
        enabled: enable,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    const isValid = isLoading;
    return [data, isValid];
}

export const useHighScoreStockList = (
    type: string,
    locKorean: boolean,
    enable = true,
    fallbackData?: any[] | null
) => {
    const { data, isValidating } = useSWR(
        enable && type ? `/api/detail/highstock?type=${type}&iskorean=${locKorean}` : null,
        fetcher,
        {
            revalidateOnMount: true,
            revalidateOnFocus: false,
            ...(fallbackData ? { fallbackData } : {}),
        }
    );
    const isValid = !data && isValidating;
    return [data, isValid];
}
export const useHighTotalScoreStockList = ({
    limit,
    loc,
    isDetail,
    fallbackData,
}: {
    limit?: number;
    loc?: string;
    isDetail?: boolean;
    /** GSSP 초기 데이터 — 있으면 priority 대기 없이 첫 HTML부터 사용 */
    fallbackData?: any[] | null;
}) => {
    const priority = usePriority(0.3);
    const { data, isValidating } = useSWR(
        priority || isDetail || !!fallbackData
            ? `/api/hightotalscore?limit=${limit}&loc=${loc || ''}`
            : null,
        fetcher,
        {
            revalidateOnMount: true,
            revalidateOnFocus: false,
            ...(fallbackData ? { fallbackData } : {}),
        }
    );

    const isValid = !data && isValidating;
    return [data, isValid];
}

// 연관 산업 데이터 요청
export const useRelatedIndustryData = (ticker: string | undefined, tucds: string[] | undefined, enable = false) => {
    const queryString = new URLSearchParams();
    tucds?.forEach((tucd) => {
        queryString.append("tucds", tucd);
    });
    const { data, isLoading } = useQuery({
        queryKey: ["detail", "relatedIndustry", ticker, tucds],
        queryFn: () => fetcher(`/api/detail/report?related=true&ticker=${ticker}&${queryString.toString()}`),
        enabled: enable,
        staleTime: 60 * 1000,
        refetchOnWindowFocus: false,
    });
    return [data, isLoading];
}